import { useNavigate } from 'react-router-dom';
import { motion } from 'motion/react';
import { Target, Eye, Rocket, Globe, Zap, Users, ShieldCheck, TrendingUp, Sparkles, ArrowRight } from 'lucide-react';
import { Footer } from '../components/Footer';

export default function VisionPage() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-[#050505] text-white font-sans overflow-x-hidden">
      <main>
        {/* HERO SECTION */}
        <section className="relative pt-40 pb-32 px-6 md:px-10 lg:px-20 overflow-hidden">
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full h-[800px] bg-gradient-to-b from-[#ff5a00]/10 to-transparent pointer-events-none opacity-50 blur-[120px]"></div>
          
          <div className="max-w-[1400px] mx-auto relative z-10">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="max-w-4xl"
            >
              <div className="inline-flex items-center space-x-2 px-5 py-2 rounded-full border border-white/10 bg-white/5 backdrop-blur-md text-[10px] font-black uppercase tracking-[0.3em] text-[#ff5a00] mb-10 shadow-2xl">
                <Sparkles className="w-3 h-3 mr-2" />
                Manifeste Festin Hub
              </div>
              
              <h1 className="text-5xl md:text-7xl lg:text-8xl font-black tracking-tight leading-[0.95] mb-12">
                Redéfinir le <br />
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-white via-white/80 to-white/40 italic">futur</span> de <br />
                l'événementiel.
              </h1>
              
              <p className="text-xl md:text-2xl text-gray-400 font-medium leading-relaxed max-w-2xl">
                Nous ne construisons pas seulement un logiciel. Nous façonnons un écosystème collaboratif où chaque organisateur et chaque exposant peut prospérer sans contraintes logistiques.
              </p>
            </motion.div>
          </div>
        </section>

        {/* MISSION & VISION */}
        <section className="py-32 px-6 md:px-10 lg:px-20 bg-white/[0.01] border-y border-white/5">
          <div className="max-w-[1400px] mx-auto grid grid-cols-1 md:grid-cols-2 gap-20">
            <motion.div 
              whileHover={{ y: -10 }}
              className="bg-white/5 rounded-[3.5rem] p-12 border border-white/10 relative overflow-hidden group shadow-2xl"
            >
              <div className="absolute -top-12 -right-12 w-48 h-48 bg-[#ff5a00]/10 rounded-full blur-3xl opacity-50 group-hover:opacity-100 transition-opacity"></div>
              <Target className="w-12 h-12 text-[#ff5a00] mb-10" />
              <h3 className="text-4xl font-black mb-6">Notre Mission</h3>
              <p className="text-xl text-gray-400 leading-relaxed font-medium">
                Simplifier et optimiser la mise en relation entre Festivals et Stands. Nous éliminons les frictions administratives pour permettre aux créateurs de se concentrer sur l'essentiel : l'expérience humaine.
              </p>
            </motion.div>

            <motion.div 
              whileHover={{ y: -10 }}
              className="bg-white/5 rounded-[3.5rem] p-12 border border-white/10 relative overflow-hidden group shadow-2xl"
            >
              <div className="absolute -top-12 -right-12 w-48 h-48 bg-blue-500/10 rounded-full blur-3xl opacity-50 group-hover:opacity-100 transition-opacity"></div>
              <Eye className="w-12 h-12 text-blue-500 mb-10" />
              <h3 className="text-4xl font-black mb-6">Notre Vision</h3>
              <p className="text-xl text-gray-400 leading-relaxed font-medium">
                Créer un écosystème collaboratif et prospère où les festivals et les stands coexistent harmonieusement. Nous visons à devenir le standard opérationnel global pour la gestion de villages événementiels.
              </p>
            </motion.div>
          </div>
        </section>

        {/* ROADMAP / LES JALONS */}
        <section className="py-40 px-6 md:px-10 lg:px-20 relative">
          <div className="max-w-[1400px] mx-auto">
            <div className="text-center mb-32">
              <div className="text-[#ff5a00] font-black uppercase tracking-[0.3em] text-xs mb-6 italic">Roadmap Stratégique</div>
              <h2 className="text-5xl md:text-7xl font-black tracking-tighter">Horizon 2035</h2>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
              {[
                { year: "2024", title: "Développement", desc: "Finalisation du moteur de recherche et des outils de gestion des inscriptions.", icon: Rocket, status: "En cours" },
                { year: "2026", title: "Lancement Global", desc: "Ouverture de la plateforme à l'ensemble du territoire français et déploiement de l'app mobile.", icon: Zap, status: "Prochainement" },
                { year: "2030", title: "International", desc: "Expansion européenne et adaptation aux régulations internationales.", icon: Globe, status: "" },
                { year: "2035", title: "Leader du Marché", desc: "Festin devient le hub central incontournable pour l'industrie événementielle mondiale.", icon: Sparkles, status: "" }
              ].map((step, i) => (
                <div key={i} className="bg-white/[0.02] border border-white/10 rounded-[3rem] p-10 relative group hover:bg-white/[0.05] transition-all">
                   <div className="text-5xl font-black text-gray-800 group-hover:text-[#ff5a00]/20 transition-colors mb-8 italic">{step.year}</div>
                   <div className="w-12 h-12 bg-white/5 rounded-2xl flex items-center justify-center border border-white/10 mb-8">
                      <step.icon className={`w-6 h-6 ${i === 0 ? 'text-[#ff5a00]' : 'text-gray-500'}`} />
                   </div>
                   <h4 className="text-2xl font-black mb-4">{step.title}</h4>
                   <p className="text-gray-500 font-medium leading-relaxed mb-6">{step.desc}</p>
                   {step.status && (
                     <div className="inline-flex items-center px-4 py-1.5 rounded-full bg-white/5 border border-white/10 text-[10px] font-black uppercase tracking-widest text-[#ff5a00]">
                        {step.status}
                     </div>
                   )}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* VALEURS */}
        <section className="py-40 px-6 md:px-10 lg:px-20 bg-white/[0.01] border-y border-white/5 overflow-hidden relative">
          <div className="absolute top-1/2 left-0 -translate-y-1/2 w-[500px] h-[500px] bg-blue-500/10 rounded-full blur-[150px] pointer-events-none"></div>
          
          <div className="max-w-[1400px] mx-auto grid grid-cols-1 lg:grid-cols-2 gap-32 items-center">
            <div>
              <div className="text-[#ff5a00] font-black uppercase tracking-[0.3em] text-xs mb-8 italic">Nos Valeurs</div>
              <h2 className="text-4xl md:text-6xl font-black mb-16 tracking-tighter leading-tight italic uppercase">
                Ancrés dans le <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-blue-600">réel.</span> <br />
                Propulsés par le <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#ff5a00] to-[#ff8c00]">futur.</span>
              </h2>
              
              <div className="space-y-12">
                {[
                  { icon: Users, title: "Collaboration", desc: "Nous croyons que la réussite collective l'emporte sur la performance individuelle." },
                  { icon: ShieldCheck, title: "Intégrité & Sécurité", desc: "La confiance est le socle de notre plateforme. Nous protégeons chaque donnée, chaque transaction." },
                  { icon: TrendingUp, title: "Durabilité", desc: "Optimiser les ressources et réduire l'impact écologique des grands événements." }
                ].map((val, i) => (
                  <div key={i} className="flex items-start space-x-8 group">
                    <div className="w-14 h-14 bg-white/5 rounded-2xl flex items-center justify-center border border-white/10 group-hover:border-[#ff5a00]/30 transition-all shrink-0">
                      <val.icon className="w-6 h-6 text-[#ff5a00]" />
                    </div>
                    <div>
                      <h4 className="text-2xl font-black mb-3">{val.title}</h4>
                      <p className="text-gray-500 font-medium leading-relaxed max-w-lg">{val.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            
            <div className="relative">
                <div className="absolute -inset-10 bg-[#ff5a00]/20 rounded-[4rem] blur-[100px] pointer-events-none"></div>
                <div className="relative aspect-square rounded-[4rem] overflow-hidden border border-white/10 shadow-2xl grayscale hover:grayscale-0 transition-all duration-1000">
                    <img src="https://images.unsplash.com/photo-1540575861501-7cf05a4b125a?q=80&w=2000&auto=format&fit=crop" alt="Vision" className="w-full h-full object-cover" />
                    <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-transparent"></div>
                    <div className="absolute bottom-12 left-12">
                        <div className="text-[10px] font-black uppercase tracking-[0.3em] text-[#ff5a00] mb-2">Impact Direct</div>
                        <div className="text-3xl font-black italic">+120 Festivals Partenaires</div>
                    </div>
                </div>
            </div>
          </div>
        </section>

        {/* CTA FINAL */}
        <section className="py-48 px-6 text-center relative overflow-hidden bg-black">
          <div className="max-w-7xl mx-auto relative z-10">
            <h2 className="text-5xl md:text-7xl lg:text-8xl font-black mb-16 tracking-tighter leading-none italic uppercase">
              Bâtissons le futur <br /> <span className="text-[#ff5a00] drop-shadow-[0_0_50px_rgba(255,90,0,0.5)]">ensemble.</span>
            </h2>
            <button 
              onClick={() => navigate('/')}
              className="bg-[#ff5a00] hover:bg-[#ff6b1a] text-white font-black py-8 px-20 rounded-[2.5rem] text-2xl transition-all shadow-[0_20px_80px_rgba(255,90,0,0.4)] active:scale-95 group"
            >
              Initialiser mon projet
              <ArrowRight className="inline-block ml-4 w-8 h-8 group-hover:translate-x-2 transition-transform" />
            </button>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
